import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../auth/auth.service';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import { countryCodes } from '../../_shared/data/countries';
import { ToastService } from '../../_core/services/toast.service';
import { CountriesPage } from '../../countries/countries.page';
import { finalize, take } from 'rxjs/operators';
import { PrivacyPolicyComponent } from '../../_shared/components/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from '../../_shared/components/terms-and-conditions/terms-and-conditions.component';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { EncrptionService } from '../encryption/encryption.service';
import { environment, PUBLIC_KEY } from '../../../environments/environment';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { trimEdgeSpaces, noLeadingSpaceValidator, trimEdgeSpacesOnBlurOrSubmit } from '../../_shared/utils/utils';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.scss'],
    standalone: true,
    imports: [IonicModule, CommonModule, ReactiveFormsModule]
})
export class SignupComponent implements OnInit {
    form: UntypedFormGroup;
    countries = countryCodes;
    isLoading = false;
    useVisibleCaptcha = false;
    visibleCaptchaToken?: string;
    visibleRecaptchaKey = environment.VISIBLE_RECAPTCHA_SITE_KEY || null;
    private trimSubscriptions: Subscription[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        private authService: AuthService,
        private alertCtrl: AlertController,
        private toastService: ToastService,
        private modalController: ModalController,
        private navCtrl: NavController,
        private recaptchaV3Service: ReCaptchaV3Service,
        private encryptionService: EncrptionService) { }

    ngOnInit() {
        this.buildForm();
    }

    ngOnDestroy() {
        this.trimSubscriptions.forEach(sub => sub.unsubscribe());
    }

    buildForm() {
        this.form = this.fb.group({
            first_name: ['', [Validators.required, Validators.minLength(2), noLeadingSpaceValidator()]],
            last_name: ['', [Validators.required, Validators.minLength(2), noLeadingSpaceValidator()]],
            country_code: ['+1', [Validators.required, Validators.pattern('([+]{1})([0-9]{1,3})')]],
            email: ['', [Validators.required, Validators.minLength(2), noLeadingSpaceValidator()]],
            phone: ['', [Validators.required, Validators.pattern('([1-9]{1})[0-9]{5,15}')]],
            password: ['', [Validators.required, Validators.minLength(6)]]
        });
        trimEdgeSpaces(this.trimSubscriptions, this.form, ['first_name', 'last_name', 'email']);
    }

    async signup() {
        if (this.form.valid) {
            this.trimSpaces();
            this.isLoading = true;
            const user = this.form.value;

            const alert = await this.alertCtrl.create({
                header: 'Get signup code',
                message: 'Send a code to ' + user.country_code + user.phone + '?',
                buttons: [
                    {
                        text: 'Send',
                        handler: () => {
                            if (this.useVisibleCaptcha && this.visibleCaptchaToken) {
                                this.registerUser(user, this.visibleCaptchaToken);
                            } else {
                                this.callInvisibleCaptcha(user)
                            }
                        }
                    },
                    {
                        text: 'Cancel',
                        role: 'cancel',
                        handler: () => this.isLoading = false
                    }
                ]
            });

            await alert.present();

        } else {
            this.handleErrors();
        }
    }

    trimSpaces() {
        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.get(key);
            if (control?.value != 'password' && control) { // Avoid trimming password field here
                trimEdgeSpacesOnBlurOrSubmit(control);
            }
        });
    }

    encryptPhoneNumber(phoneNumber: number) {
        const encryptedData = this.encryptionService.encryptPhoneWithRSA_AES(phoneNumber.toString(), PUBLIC_KEY);
        return encryptedData;
    }

    captchaResolved(token: string) {
        this.visibleCaptchaToken = token;
    }

    registerUser(user, recaptchaToken: string) {
        const phone = this.encryptPhoneNumber(user.phone);
        const userRequest = {
            first_name: user.first_name,
            last_name: user.last_name,
            country_code: user.country_code,
            phone: phone.encryptedPhone,
            secret_key: phone.encryptedRSAKey,
            password: user.password,
            isInvisibleCaptcha: !this.useVisibleCaptcha,
        };

        this.authService
            .signup(userRequest, recaptchaToken)
            .pipe(
                take(1),
                finalize(() => this.isLoading = false),
            )
            .subscribe({
                next: res => {
                    localStorage.removeItem('redirectAfterLogin');
                    this.toastService.presentToast(res.message);
                    this.authService.castAuthState({
                        login: false,
                        signup: false,
                        verify: true,
                        phone: user.phone,
                        country_code: user.country_code
                    });
                }, error: (responseError) => {
                    if (responseError?.error?.code === 'CAPTCHA_VALIDATION_FAILED' && responseError.status === 422) {
                        this.useVisibleCaptcha = true;
                    } else if (responseError.message) {
                        this.toastService.presentToast(responseError.message);
                    }
                }
            });
    }

    private callInvisibleCaptcha(user) {
        this.recaptchaV3Service
            .execute('registerUser')
            .pipe(take(1))
            .subscribe((invisibleCaptchaToken) => {
                this.registerUser(user, invisibleCaptchaToken);

            });
    }

    async handleErrors() {
        let errorMessage = '';
        if (this.form.get('first_name')?.invalid) {
            errorMessage = 'First name must have minimum 2 characters.';
        } else if (this.form.get('last_name')?.invalid) {
            errorMessage = 'Last name must have minimum 2 characters.';
        } else if (this.form.get('country_code')?.invalid) {
            errorMessage = 'Country code must start with + followed by 1 to 3 digits';
        } else if (this.form.get('phone')?.invalid) {
            errorMessage = 'Phone must include area code and phone number. Digits only. If your first digit is 0 remove it.';
        } else if (this.form.get('password')?.invalid) {
            errorMessage = 'Password must have minimum 6 characters.';
        }
        this.toastService.presentDangerToast(errorMessage);
    }

    async getCountryCode() {
        const modal = await this.modalController.create({
            component: CountriesPage
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data) {
            this.form.get('country_code')?.setValue(data);
        }
    }

    async openTermsAndConditions() {
        const modal = await this.modalController.create({
            component: TermsAndConditionsComponent,
        });

        await modal.present();
    }

    async openPrivacyPolicy() {
        const modal = await this.modalController.create({
            component: PrivacyPolicyComponent,
        });

        await modal.present();
    }

}
